import type { AutonomyLevel } from './assistant';
import { RUN_TOKEN_CAP_DEFAULT, type RunLogResult } from './assistant-missions';
import type { Recurrence } from './assistant-proactive';
import type { Id, ISODate } from './common';

// S7 standing orders (spec §7, §9.2, L4): the DTOs, the request bodies' shapes and the `order` confirm card's value.
// The request bodies are parsed by the API's zod schemas (`modules/assistant/orders/schema.ts`), which take their
// bounds from here.

/** 027 `assistant_standing_orders.trigger` and `.event_kind`. */
export const ORDER_TRIGGERS = ['schedule', 'event'] as const;
export type OrderTrigger = (typeof ORDER_TRIGGERS)[number];
export const ORDER_EVENT_KINDS = ['assigned_to_me', 'comment_on_mine', 'status_in_branch', 'overdue_mine'] as const;
export type OrderEventKind = (typeof ORDER_EVENT_KINDS)[number];
/**
 * P5 (D181): why the worker turned an order off — three of its own failures in a row, its owner lost the assistant,
 * its branch is gone (deleted, purged or no longer visible to the owner, D187), its series ended (P10). Stored on the
 * order when the worker turns it off (028 `off_reason`, D219); an order its owner turned off, or turned on again, has
 * none.
 */
export const ORDER_OFF_REASONS = ['errors', 'access', 'branch', 'ended'] as const;
export type OrderOffReason = (typeof ORDER_OFF_REASONS)[number];

/** §7.1, §11 `ORDER_LIMIT`: orders a person may have on at once. */
export const ORDER_LIMIT = 20;

/**
 * S7 §7.1, §8.2 (P3, D179): the standing-order tools — `order.*` and `orders.list`. A background run is neither offered
 * nor allowed any of them (the agent's `runOffers`, the guard's `runMayCall`), and none is on MCP (`mcp: false`).
 */
export const isOrderCapability = (name: string): boolean => name.startsWith('order.') || name === 'orders.list';
/** 027's CHECKs on the order (name and instruction counted after trimming). */
export const ORDER_BOUNDS = {
  name: { min: 1, max: 100 },
  instruction: { min: 1, max: 2000 },
  runsPerDay: { min: 1, max: 24 },
  runTokenCap: { min: 5000, max: 200_000 },
} as const;
/** A new order's defaults: 027's `runs_per_day`; the token cap is D249's, not 027's column DEFAULT. */
export const ORDER_DEFAULTS = { runsPerDay: 10, runTokenCap: RUN_TOKEN_CAP_DEFAULT } as const;
/** P8: the ids of each kind an order may name as "Dùng" (informational, never a restriction on the run). */
export const ORDER_USES_MAX = 10;

/** P8: what the order expects to use, as ids; the server resolves them to names the owner sees. */
export type OrderUses = { serverIds?: Id[]; projectIds?: Id[] };
/** One resolved "Dùng" item (hidden = missing: one the owner cannot see is dropped). */
export type OrderUseItem = { kind: 'server' | 'project'; id: Id; name: string };

/**
 * One row of `GET /api/assistant/orders`. Schedule fields (`recurrence`, `anchorLocal` 'YYYY-MM-DDTHH:MM', `time`,
 * `startDate`, `nextAt`) are null on an event order, the event fields on a schedule one. `eventItem` is the branch root
 * the owner sees (null when gone or hidden). `offReason` only while the order is off (P5). `last` is its newest run,
 * worded as the run log words it; `live` whether a run is queued or running (Chạy thử ngay → `RUN_ALIVE`).
 */
export type OrderDTO = { id: Id; name: string; instruction: string; trigger: OrderTrigger;
  recurrence: Recurrence | null; anchorLocal: string | null; time: string | null; startDate: ISODate | null;
  eventKind: OrderEventKind | null; eventItem: { id: Id; title: string } | null; level: AutonomyLevel; active: boolean;
  offReason: OrderOffReason | null; runsPerDay: number; runTokenCap: number; failures: number; nextAt: string | null;
  last: { runId: Id; at: string; result: RunLogResult } | null; live: boolean; version: number };

/**
 * `POST /api/assistant/orders` and `order.create`'s input (§7.1, P6, P8). A schedule takes `time` 'HH:mm', an optional
 * `startDate` (default: today in HCM while `time` is still ahead, else tomorrow) and a `recurrence`; an event takes
 * `eventKind`, and `eventItemId` exactly when it is `status_in_branch`. `level` absent = the owner's own.
 */
export type OrderCreateBody = { name: string; instruction: string; trigger: OrderTrigger; recurrence?: Recurrence;
  time?: string; startDate?: ISODate; eventKind?: OrderEventKind; eventItemId?: Id; level?: AutonomyLevel;
  runsPerDay?: number; runTokenCap?: number; uses?: OrderUses };
/** `POST /api/assistant/orders/:id/run` ("Chạy thử ngay", P18): the `manual` run it queued. */
export type OrderRanDTO = { id: Id; runId: Id };
/** `PATCH /api/assistant/orders/:id`: any field of the create, `active`, and the version the page saw (P17). */
export type OrderPatchBody = Partial<Omit<OrderCreateBody, 'uses'>> & { active?: boolean; version: number };

/**
 * The `order` confirm field's value (P7, D201): `instruction` verbatim, `when` the schedule or event words, `uses` the
 * resolved items (P8), `levelLine` the sentence that sums up the level. `Sửa` changes `name` and `instruction` only.
 */
export type OrderCardValue = { name: string; instruction: string; when: string; uses: OrderUseItem[];
  level: AutonomyLevel; levelLine: string };

/**
 * ★ invented copy (P21, D197; for the PO copy pass): the `order` card's "Khi nào" words for each event. The server
 * builds the card, so they live here; the web's `missions/labels.ts` reads them from here. The branch line names the
 * branch root's title.
 */
export const ORDER_EVENT_WHEN: Record<Exclude<OrderEventKind, 'status_in_branch'>, string> = {
  assigned_to_me: 'Khi có việc giao cho tôi',
  comment_on_mine: 'Khi có bình luận trên việc của tôi',
  overdue_mine: 'Khi việc của tôi quá hạn',
};
export const orderBranchWhen = (title: string): string => `Khi trạng thái trong nhánh “${title}” đổi`;
/** ★ invented copy (P21, D197): the sentence the `order` card shows under its level. */
export const ORDER_LEVEL_LINE: Record<AutonomyLevel, string> = {
  ask: 'Luôn hỏi — mọi thao tác ghi đều hỏi anh trước; đọc vẫn chạy luôn.',
  light: 'Tự làm việc nhẹ — việc nhẹ chạy luôn, có Hoàn tác; việc nặng, ghi ra ngoài, tài chính hỏi anh.',
  bounded: 'Tự làm trong giới hạn — thêm tài chính dưới ngưỡng và tool ngoài anh chọn; việc chạm tới người khác vẫn '
    + 'hỏi anh.',
};
